import { processFreeGames } from "quickfire/integration/builders/record/process-free-games";
import recoveryRecordBuilder from "quickfire/integration/builders/record/recovery-record-builder";
import { convertQFGridToSlotworksGrid } from "quickfire/integration/util/quickfire-utils";
import quickfireModel from "quickfire/model/quickfire-model";
import qfSignals from "quickfire/qf-signals";
import { VEventEnum } from "quickfire/v/enums";
import betModel from "slotworks/model/bets/bet-model";
import Gamestate from "slotworks/model/game-state";
import Gameplay from "slotworks/model/gameplay/gameplay";
import Record from "slotworks/model/gameplay/records/record";
import SpinRecord from "slotworks/model/gameplay/records/spin-record";
import AuthRequestPayload from "slotworks/model/gameplay/requests/auth-request-payload";
import { RecordBuilder } from "slotworks/server/builders/record-builder";
import { asArray, lastInArray } from "slotworks/utils/collection-utils";

class RefreshRecordBuilder implements RecordBuilder {
    public filter(request: any, response: any): boolean {
        return request instanceof AuthRequestPayload && !recoveryRecordBuilder.filter(request, response);
    }

    public build(gameplay: Gameplay, request: any, packet: QuickfireResponsePacket) {
        const response = packet.Response;
        const bet = response.Bet.attributes;
        const player = response.Player.attributes;

        betModel.creditSizes.values = bet.validChips.split(",").map((value) => parseInt(value));
        betModel.creditsPerLine.values = this.getValidNumChips(bet);

        const chipSize = parseInt(bet.chipSize);
        const forcedChipSize = this.getValidChipSize(parseInt(bet.chipSize));

        if (chipSize !== forcedChipSize) {
            quickfireModel.forcedChipSize = forcedChipSize;
        }

        if (bet.multiplier) {
            betModel.setFixedBetCost(parseInt(bet.multiplier));
        }

        gameplay.startBalance = gameplay.balance = Math.floor(player.balance);

        qfSignals.dispatch(VEventEnum.playerInformationRecieved);

        if (response.FreeGames && response.FreeGames.LastRealWager) {
            const lastRealBet = response.FreeGames.LastRealWager;
            betModel.setDefaults(parseInt(lastRealBet.attributes.numChips), lastRealBet.attributes.activePaylines.split(",").length, parseInt(lastRealBet.attributes.chipSize));
        } else {
            betModel.setDefaults(parseInt(bet.numChips), bet.activePaylines.split(",").length, chipSize);
        }

        processFreeGames(response);

        return this.processInitialReelPositions(response) as Record[];
    }

    private processInitialReelPositions(response: QuickfireResponse) {
        if (Gamestate.getCurrentGame().isRecovered) {
            return [];
        }

        const lastRecord = new SpinRecord();

        // Initial reel stops
        if (response.Slot) {
            if (response.Slot.ResultSets) {
                const resultSet = response.Slot.ResultSets;
                const results = asArray(resultSet.Result);
                for (let resultIndex = results.length - 1; resultIndex >= 0; resultIndex--) {
                    const result = results[resultIndex];
                    if (result.Reels) {
                        const reel = lastInArray(asArray(result.Reels));
                        lastRecord.grid = convertQFGridToSlotworksGrid(reel.attributes.symbols);
                        break;
                    }
                }
            }
            if (response.Slot.attributes.transTotalWin) {
                if (!this.responseHasCancelledFreegame(response) && !this.newFreegamePendingNext()) {
                    lastRecord.cashWon = parseInt(response.Slot.attributes.transTotalWin);
                }
            }
        }

        return [lastRecord];
    }

    private responseHasCancelledFreegame(response: QuickfireResponse) {
        if (response.FreeGames && response.FreeGames.CurrentGame) {
            const qfCurrentFreeGame = response.FreeGames.CurrentGame;

            for (const freeGame of quickfireModel.freeGames) {
                if (freeGame.offerId === parseInt(qfCurrentFreeGame.attributes.offerId)) {
                    return false;
                }
            }

            return true;
        }

        return false;
    }

    private newFreegamePendingNext() {
        const haveFreeGames = quickfireModel.freeGames && quickfireModel.freeGames[0];
        return (haveFreeGames && quickfireModel.freeGames[0].gamesPlayed === 0) && !quickfireModel.playingFreeGames;
    }

    private getValidNumChips(bet: QuickfireBetAttributes) {
        const maxChips = parseInt(bet.maxChips);
        const minChips = parseInt(bet.minChips);
        const validChips = [];
        for (let chips = minChips; chips <= maxChips; chips++) {
            validChips.push(chips);
        }
        return validChips;
    }

    private getValidChipSize(chipSize: number) {
        if (betModel.creditSizes.values.indexOf(chipSize) === -1) {
            let closestChipSize = 0;
            betModel.creditSizes.values.forEach((value: number) => {
                if (closestChipSize === 0 || Math.abs(value - chipSize) < Math.abs(value - closestChipSize)) {
                    closestChipSize = value;
                }
            });

            return closestChipSize;
        }

        return chipSize;
    }
}

export default new RefreshRecordBuilder();
